<template>
  <v-container class="pa-0">
    <v-row dense v-if="!recurring">
      <v-col cols="6">
        <date-picker v-model="invoice.date" label="Date" @change="getExchangeRate();setDueDate()" date-only/>
      </v-col>
      <v-col cols="6" v-if="preferences.useNumber">
        <v-text-field label="Number" v-model="invoice.number" outlined dense :rules="[fieldRules.required,fieldRules.maxLength(20)]" :counter="20"/>
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="6">
        <v-autocomplete label="Type" v-model="invoice.paymentMethodId" :items="paymentMethods" item-value="id" item-text="name" outlined dense
                  @change="eligibleAccounts.length > 0 ? invoice.accountRef=eligibleAccounts[0].ref : ''">
          <template v-slot:item="{item}">
            <v-list-item-content>{{item.name}}</v-list-item-content>
            <v-list-item-action class="text-caption">{{item.id===''?'Invoice':'Sales receipt'}}</v-list-item-action>
          </template>
        </v-autocomplete>
      </v-col>
      <v-col cols="6" v-if="connectedToAccounting && invoice.paymentMethodId!=='' && eligibleAccounts.length>1 && invoice.qbo ">
        <account-picker v-model="invoice.accountRef"
                        :label="invoice.paymentMethodId==='' ? 'A/R' : 'Account'"
                        :accounts="eligibleAccounts"/>
      </v-col>
    </v-row>

    <v-row dense v-if="invoice.paymentMethodId===''">
      <v-col cols="6">
        <v-autocomplete v-model="invoice.termId" outlined dense label="Terms" @change="setDueDate()"
                        :items="terms" item-text="name" item-value="id"/>
      </v-col>
      <v-col cols="6" v-if="!recurring">
        <date-picker v-model="invoice.due" label="Due" date-only/>
      </v-col>
    </v-row>

    <v-row dense v-else>
      <v-col cols="6">
        <v-text-field label="Payment reference" v-model="invoice.paymentRefNum" outlined dense :rules="[fieldRules.maxLength(20)]" counter="21" hint="e.g. cheque number"/>
      </v-col>
    </v-row>

    <v-row dense v-if="connectedToAccounting">
      <v-col cols="6">
        <v-checkbox label="Create in QuickBooks" v-model="invoice.qbo" class="mt-1"/>
      </v-col>
      <v-col cols="6">
        <v-checkbox label="Allow online payments" v-model="invoice.allowOnlinePayments" class="mt-1" :disabled="!invoice.qbo"/>
      </v-col>
    </v-row>

      <v-row dense v-if="salesReps.length > 1">
      <v-col cols="6">
        <v-autocomplete v-model="invoice.salesRepId" outlined dense label="Sales representative" :disabled="!!restrictedToSalesRepId"
                        :items="salesReps" item-text="name" item-value="id"/>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import {format, add} from 'date-fns'

export default {
  name: 'SalesInvoicesDetails',
  props: {
    invoice: {type:Object, required:true},
    preferences: {type:Object, required:true},
    customer: {type:Object, required:false},
    restrictedToSalesRep: {type:String, required:false},
    recurring: {type:Boolean, required:true},
    cc: {type:String, required:true},
    bcc: {type:String, required:true}
  },
  data() {
    return {
      paymentMethods: [{id:'',name:'Invoice'},{divider:true}],
      terms: [{id:'',name:''}],
      salesReps: [{id:'',name:'',email:''}],
      accounts: [],
      ready: false,
      restrictedToSalesRepId: 0,
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    connectedToAccounting() {
      return this.company.connectedToAccounting;
    },
    eligibleAccounts() {
      return this.accounts.filter(account => account.currency === (this.customer?.currency ?? this.company.settings.homeCurrency) &&
                                              (this.invoice.paymentMethodId === '' && account.type === 'AccountsReceivable' ||
                                               this.invoice.paymentMethodId !== '' && (account.type === 'Bank' || account.type === 'CreditCard' || account.type === 'OtherCurrentAsset')));
    },
  },
  watch: {
    customer() {
      if (!this.ready) {    // On init, don't reset everything
        this.ready = true;
        if (!!this.invoice.id)
          return;
      }
      if (!this.customer)
        return;
      this.invoice.accountRef = this.eligibleAccounts.length > 0 ? this.eligibleAccounts[0].ref : '';
      this.invoice.termId = this.customer.term?.id ?? '';
      this.invoice.salesRepId = this.customer.salesRep?.id ?? '';
      this.getExchangeRate();
      this.setDueDate();
    },
    'invoice.date':function() {
      this.getExchangeRate();
      this.setDueDate();
    },
    'invoice.termId':function() {
      this.setDueDate();
    },
    'invoice.draft':function() {
      if (!this.invoice.id || this.invoice.draft)
        this.invoice.qbo = this.connectedToAccounting && (this.preferences?.defaultQBO ?? true);
    },
    'invoice.salesRepId'() {
      const salesRep = this.salesReps.find(rep => rep.id === this.invoice.salesRepId) || '';
      if (this.preferences.defaultCcSalesRep)
        this.$emit('update:cc', salesRep.email);
      if (this.preferences.defaultBccSalesRep)
        this.$emit('update:bcc', salesRep.email);
    },
    'preferences.defaultQBO': function() {
      this.invoice.qbo = this.connectedToAccounting && !this.invoice.id  ? this.preferences.defaultQBO : this.invoice.qbo;
    },
    'preferences.defaultAllowOnlinePayments': function() {
      this.invoice.allowOnlinePayments = this.connectedToAccounting && !this.invoice.id  ? this.preferences.defaultAllowOnlinePayments : this.invoice.allowOnlinePayments;
    },
    'preferences.useNumber': function() {
      if (!this.preferences.useNumber)
        delete this.invoice.number;
    }
  },
  async created() {
    await this.$lazyListLoad(this.salesReps, 'salesReps2', 'id,name,email,userId,restricted', 100)
    this.$lazyListLoad(this.terms, 'terms2', 'id,name,standard,dueDays,dueNextMonthDays,dayOfMonthDue', 100)
    this.$lazyListLoad(this.paymentMethods, 'paymentMethods2', 'id,name', 100)

    const results = await this.$apiCall(`query SalesInvoicesDetails {
                                            invoiceNextNumber
                                            accounts {
                                              ref,name,type,typeName,currency
                                            }
                                          }`,
                                        {});
    if (!results.error) {
      this.restrictedToSalesRepId = this.salesReps.find(rep => rep.userId === this.$store.state.user.sub && rep.restricted)?.id || '';
      this.$emit('update:restrictedToSalesRep', this.restrictedToSalesRepId);
      this.accounts = results.data.accounts;
      if (!this.invoice.id) {
        this.invoice.date = format(new Date(), 'yyyy-MM-dd');
        this.invoice.number = results.data.invoiceNextNumber;
        this.invoice.qbo = this.connectedToAccounting && (this.preferences?.defaultQBO ?? true);
        this.invoice.allowOnlinePayments = this.connectedToAccounting && (this.preferences?.allowOnlinePayments ?? true);
        this.invoice.salesRepId = this.restrictedToSalesRepId;
      }
    }
  },
  methods: {
    async getExchangeRate() {
      if (this.customer && this.customer.currency !== this.company.settings.homeCurrency) {
        const results = await this.$apiCall(`query SalesInvoicesDetails($code:String!,$date:Date!) {
                                                currencyRate(code:$code,date:$date)
                                              }`,
                                              {code:this.customer.currency,date:this.invoice.date.substring(0,10)});
        if (!results.error)
          this.invoice.exchangeRate = results.data.currencyRate;
      }
    },
    setDueDate() {
      if (!this.invoice.date)   // Not sure this is possible, but if not set yet we'll have a problem
        return;

      const term = this.terms.find(term => term.id === this.invoice.termId);

      if (term?.standard === undefined)
        this.invoice.due = this.invoice.date;

      else if (term.standard)
        try {
          this.invoice.due = format(add(new Date(`${this.invoice.date}T00:00:00`), {days:term.dueDays}), 'yyyy-MM-dd');
        } catch (error) {
          let d = new Date(`${this.invoice.date}T00:00:00`);
          if (d.toString() === 'Invalid Date')
            throw new Error(`${error.message}: new Date(${this.invoice.date}T00:00:00)`);
          d = add(d, {days:term.dueDays});
          if (d.toString() === 'Invalid Date')
            throw new Error(`${error.message}: add ${term.dueDays}`);
          throw new Error(`${error.message}: format`);
        }


      else {
        const day = parseInt(this.invoice.date.substr(8,2));
        let prefix;
        if (day + term.dueNextMonthDays??0 > term.dayOfMonthDue)
          prefix = format(add(new Date(this.invoice.date+'T00:00:00'), {months:1}), 'yyyy-MM-')
        else
          prefix = this.invoice.date.substr(0,8)
        this.invoice.due = `${prefix}${day < 10 ? `0${term.dayOfMonthDue}` : term.dayOfMonthDue}`;
      }
    }
  }
}
</script>